import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { DataContext } from '../context/DataProvider';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import EnrollmentModal from '../components/common/EnrollmentModal';
import CountdownTimer from '../components/common/CountdownTimer';

const CourseDetail = () => {
    const { courseId } = useParams();
    const { courses } = useContext(DataContext);
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

    useEffect(() => {
        // Handle both number and string IDs in case data source changes or URL param type varies
        const foundCourse = courses.find(c => c.id === parseInt(courseId) || c.id === courseId);
        if (foundCourse) {
            setCourse(foundCourse);
        }
    }, [courseId, courses]);

    if (!course) {
        return (
            <Section className="pt-32 pb-20 bg-black text-center h-screen flex flex-col justify-center items-center">
                <h1 className="text-3xl font-bold text-white mb-4">Course Not Found</h1>
                <p className="text-gray-400 mb-8">The course you are looking for does not exist.</p>
                <Button onClick={() => navigate('/e-learning')} className="text-sm px-6 py-3">
                    Back to E-Learning
                </Button>
            </Section>
        );
    }

    return (
        <>
            <Helmet>
                <title>{course.title} | Course Detail</title>
                <meta name="description" content={`Detailed information about ${course.title} - ${course.desc}`} />
            </Helmet>

            {/* 1. TOP SECTION: Hero Image & Title */}
            <section className="pt-32 pb-16 bg-black text-center relative border-b border-gray-800">
                <div className="container mx-auto px-4 max-w-5xl">
                    <span className="inline-block mb-6 text-xs font-bold px-4 py-1.5 bg-[#bebd19] text-black rounded-full uppercase tracking-wider">
                        {course.level}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight">{course.title}</h1>
                    
                    {course.thumbnail_url ? (
                        <div className="relative rounded-2xl overflow-hidden border border-gray-800 shadow-2xl aspect-video mx-auto">
                            <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                             <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        </div>
                    ) : (
                        <div className="w-full aspect-video bg-[#111] rounded-2xl border border-gray-800 flex items-center justify-center mx-auto">
                            <span className="text-gray-600">No Preview Available</span>
                        </div>
                    )}

                    <div className="flex justify-center flex-wrap gap-6 mt-8 text-sm uppercase tracking-wide font-medium">
                        <span className="text-gray-400 flex items-center gap-2">
                             <span className="w-2 h-2 rounded-full bg-[#bebd19]"></span>
                             {course.format}
                        </span>
                    </div>
                </div>
            </section>

            {/* 2. MIDDLE SECTION: Course Description */}
            <Section className="bg-[#050505] py-16">
                 <div className="container mx-auto px-4 max-w-4xl">
                    <div className="prose prose-lg prose-invert max-w-none mx-auto [&_*]:!text-gray-400 [&_h1]:!text-white [&_h2]:!text-white [&_h3]:!text-white [&_strong]:!text-green-400 [&_li]:!text-gray-400 [&_ul]:!text-gray-400 [&_ol]:!text-gray-400"
                        dangerouslySetInnerHTML={{ __html: course.desc }}
                    />
                 </div>
            </Section>

            {/* 3. LAST SECTION: Enrollment Card */}
            <Section className="bg-black py-24 relative overflow-hidden">
                {/* Background Decor */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-[#bebd19]/5 blur-[120px] rounded-full pointer-events-none"></div>

                <div className="container mx-auto px-4 max-w-4xl relative z-10">
                    <div className="bg-zinc-900/50 backdrop-blur-xl p-8 md:p-12 rounded-3xl border border-white/10 text-center shadow-2xl relative">
                        
                        <div className="inline-block mb-4 px-4 py-1 rounded-full bg-[#bebd19]/10 border border-[#bebd19]/20 text-[#bebd19] text-sm font-semibold tracking-wide uppercase">
                            Limited Time Offer
                        </div>
                        
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Ready to Master this Skill?</h2>
                        <p className="text-gray-400 mb-10 text-lg max-w-2xl mx-auto">
                            Join hundreds of students who have already transformed their careers. Secure your spot in the next batch today.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-w-2xl mx-auto">
                            {/* Price Box */}
                            <div className="bg-black/40 p-6 rounded-2xl border border-white/5 flex flex-col justify-center items-center hover:border-[#bebd19]/30 transition-colors">
                                <p className="text-gray-500 text-xs uppercase tracking-widest mb-2 font-bold">Total Investment</p>
                                <p className="text-white text-4xl lg:text-5xl font-bold flex items-start leading-none">
                                    <span className="text-2xl text-[#bebd19] mr-1 mt-1">&#8377;</span>
                                    {course.price.replace(/[^0-9.]/g, '')}
                                </p>
                            </div>

                            {/* Date Box */}
                            {course.batch_date && (
                                <div className="bg-black/40 p-6 rounded-2xl border border-white/5 flex flex-col justify-center items-center hover:border-[#bebd19]/30 transition-colors">
                                    <p className="text-gray-500 text-xs uppercase tracking-widest mb-2 font-bold">Next Batch Starts</p>
                                    <p className="text-white text-2xl font-bold mb-1">
                                        {new Date(course.batch_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}
                                    </p>
                                    {(new Date(course.batch_date) - new Date()) <= (2 * 24 * 60 * 60 * 1000) && (
                                        <div className="text-[#bebd19] text-sm font-medium">
                                            <CountdownTimer targetDate={course.batch_date} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        <Button
                            variant={course.status === 'Enroll Now' ? 'primary' : 'outline'}
                            className="w-full md:w-auto md:px-16 text-xl py-4 font-bold rounded-full shadow-lg shadow-[#bebd19]/20 hover:shadow-[#bebd19]/40 transform hover:-translate-y-1 transition-all duration-300"
                            onClick={() => setIsEnrollModalOpen(true)}
                        >
                            {course.status}
                        </Button>
                        <div className="mt-6 flex items-center justify-center gap-2 text-gray-500 text-sm">
                            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Secure Payment</span>
                            <span className="w-1 h-1 rounded-full bg-gray-700"></span>
                            <span>Instant Access</span>
                        </div>
                    </div>
                </div>
            </Section>

             <EnrollmentModal
                isOpen={isEnrollModalOpen}
                onClose={() => setIsEnrollModalOpen(false)}
                course={course}
            />
        </>
    );
};

export default CourseDetail;
